import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Helper to format error messages
export const formatApiError = (error) => {
  const detail = error?.response?.data?.detail;
  if (detail == null) return 'Algo salió mal. Intenta de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Clients API
export const clientsApi = {
  getAll: () => axios.get(`${API}/clients`),
  getOne: (id) => axios.get(`${API}/clients/${id}`),
  create: (data) => axios.post(`${API}/clients`, data),
  update: (id, data) => axios.put(`${API}/clients/${id}`, data),
  delete: (id) => axios.delete(`${API}/clients/${id}`),
};

// Vehicles API
export const vehiclesApi = {
  getAll: () => axios.get(`${API}/vehicles`),
  getOne: (id) => axios.get(`${API}/vehicles/${id}`),
  create: (data) => axios.post(`${API}/vehicles`, data),
  update: (id, data) => axios.put(`${API}/vehicles/${id}`, data),
  delete: (id) => axios.delete(`${API}/vehicles/${id}`),
  getProfitability: (id) => axios.get(`${API}/vehicles/${id}/profitability`),
  getAllProfitability: (params) => axios.get(`${API}/vehicles/profitability/all`, { params }),
  exportExcelUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/vehicles/export/excel${qs ? `?${qs}` : ''}`;
  },
};

// Contracts API
export const contractsApi = {
  getAll: () => axios.get(`${API}/contracts`),
  getOne: (id) => axios.get(`${API}/contracts/${id}`),
  create: (data) => axios.post(`${API}/contracts`, data),
  update: (id, data) => axios.put(`${API}/contracts/${id}`, data),
  getSchedule: (id) => axios.get(`${API}/contracts/${id}/schedule`),
  getProgress: () => axios.get(`${API}/contracts/progress/all`),
};

// Payments API
export const paymentsApi = {
  getAll: (contractId) => axios.get(`${API}/payments`, { params: { contract_id: contractId } }),
  create: (data) => axios.post(`${API}/payments`, data),
  update: (id, data) => axios.patch(`${API}/payments/${id}`, data),
  delete: (id) => axios.delete(`${API}/payments/${id}`),
  clearPenalty: (id) => axios.delete(`${API}/payments/${id}/penalty`),
  weeklyStatus: (contractId, weekNumber) => axios.get(`${API}/payments/weekly-status/${contractId}`, { params: weekNumber != null ? { week_number: weekNumber } : {} }),
  condone: (data) => axios.post(`${API}/payments/condone`, data),
  auditDuplicateWeeks: () => axios.get(`${API}/payments/audit/duplicate-weeks`),
  mergeDuplicateWeeks: (body) => axios.post(`${API}/payments/audit/merge-duplicates`, body),
  pendingCollection: () => axios.get(`${API}/payments/pending-collection`),
  exportPendingCollection: (scheduleIds) => axios.post(`${API}/payments/pending-collection/export`, { schedule_ids: scheduleIds || [] }, { responseType: 'blob' }),
  updateSchedulePenalty: (sid, amount) => axios.patch(`${API}/payments/schedule/${sid}/penalty`, { penalty_amount: amount }),
  deleteSchedulePenalty: (sid) => axios.delete(`${API}/payments/schedule/${sid}/penalty`),
  updateScheduleAccessory: (sid, amount, note) => axios.patch(`${API}/payments/schedule/${sid}/accessory`, { accessory_amount: amount, accessory_note: note }),
  deleteScheduleAccessory: (sid) => axios.delete(`${API}/payments/schedule/${sid}/accessory`),
  statementPdfUrl: (clientId, params) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/payments/statement/${clientId}/pdf?${qs}`;
  },
  statementExcelUrl: (clientId, params) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/payments/statement/${clientId}/excel?${qs}`;
  },
};

// Creditors API
export const creditorsApi = {
  getAll: () => axios.get(`${API}/creditors`),
  getOne: (id) => axios.get(`${API}/creditors/${id}`),
  create: (data) => axios.post(`${API}/creditors`, data),
  update: (id, data) => axios.put(`${API}/creditors/${id}`, data),
  delete: (id) => axios.delete(`${API}/creditors/${id}`),
  getMonthlySummary: () => axios.get(`${API}/creditors/_/monthly-summary`),
  recordPayment: (id, payload) => axios.post(`${API}/creditors/${id}/payments`, payload),
  getPayments: (id) => axios.get(`${API}/creditors/${id}/payments`),
  getSummary: () => axios.get(`${API}/creditors/summary/totals`),
  persistSnapshot: (items) => axios.post(`${API}/creditors/persist-snapshot`, { items }),
  backfillArrendamientoComponents: () => axios.post(`${API}/creditors/backfill-arrendamiento-payment-components`),
  registerArrendamientoThrough: (target_date) => axios.post(`${API}/creditors/register-arrendamiento-payments-through`, { target_date }),
  deleteCreditorPayment: (creditorId, paymentId) => axios.delete(`${API}/creditors/${creditorId}/payments/${paymentId}`),
  exportExcelUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/creditors/export/excel${qs ? `?${qs}` : ''}`;
  },
  bulkLinkVehiclesFromExcel: (formData) => axios.post(`${API}/creditors/bulk-link-vehicles`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getAmortization: (id) => axios.get(`${API}/creditors/${id}/amortization`),
  exportAmortizationExcel: (id) => `${API}/creditors/${id}/amortization/excel`,
  exportAmortizationPdf: (id) => `${API}/creditors/${id}/amortization/pdf`,
};

// Vehicle Expenses API
export const expensesApi = {
  getAll: (vehicleId) => axios.get(`${API}/expenses`, { params: vehicleId ? { vehicle_id: vehicleId } : {} }),
  create: (data) => axios.post(`${API}/expenses`, data),
  delete: (id) => axios.delete(`${API}/expenses/${id}`),
};

// Kilometraje API
export const kilometersApi = {
  getAll: (vehicleId) => axios.get(`${API}/vehicle-kilometers`, { params: vehicleId ? { vehicle_id: vehicleId } : {} }),
  create: (data) => axios.post(`${API}/vehicle-kilometers`, data),
  update: (id, data) => axios.put(`${API}/vehicle-kilometers/${id}`, data),
  delete: (id) => axios.delete(`${API}/vehicle-kilometers/${id}`),
  monthlyComparison: (vehicleId) => axios.get(`${API}/vehicle-kilometers/monthly-comparison`, { params: vehicleId ? { vehicle_id: vehicleId } : {} }),
};

// Maintenance API
export const maintenanceApi = {
  getAll: (vehicleId) => axios.get(`${API}/vehicle-maintenance`, { params: vehicleId ? { vehicle_id: vehicleId } : {} }),
  create: (data) => axios.post(`${API}/vehicle-maintenance`, data),
  update: (id, data) => axios.put(`${API}/vehicle-maintenance/${id}`, data),
  delete: (id) => axios.delete(`${API}/vehicle-maintenance/${id}`),
  status: () => axios.get(`${API}/vehicle-maintenance/status`),
  exportExcelUrl: (vehicleId, startDate, endDate) => {
    const qs = new URLSearchParams();
    if (vehicleId) qs.set('vehicle_id', vehicleId);
    if (startDate) qs.set('start_date', startDate);
    if (endDate) qs.set('end_date', endDate);
    const q = qs.toString();
    return `${API}/vehicle-maintenance/export/excel${q ? `?${q}` : ''}`;
  },
};

// Fleet Control API
export const fleetApi = {
  getAll: (params) => axios.get(`${API}/fleet`, { params }),
  getSummary: () => axios.get(`${API}/fleet/summary`),
};

// Documents API
export const documentsApi = {
  // Contract documents
  listContractDocs: (contractId) => axios.get(`${API}/documents/contracts`, { params: contractId ? { contract_id: contractId } : {} }),
  uploadContractDoc: (formData) => axios.post(`${API}/documents/contracts/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadContractDoc: (fileId) => `${API}/documents/contracts/download/${fileId}`,
  deleteContractDoc: (fileId) => axios.delete(`${API}/documents/contracts/${fileId}`),
  // Client documents
  listClientDocs: (clientId) => axios.get(`${API}/documents/clients`, { params: clientId ? { client_id: clientId } : {} }),
  uploadClientDoc: (formData) => axios.post(`${API}/documents/clients/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateClientDocMeta: (fileId, payload) => axios.patch(`${API}/documents/clients/${fileId}`, payload),
  markDocNotApplicable: (payload) => axios.post(`${API}/documents/clients/mark-not-applicable`, payload),
  downloadClientDoc: (fileId) => `${API}/documents/clients/download/${fileId}`,
  previewClientDoc: (fileId) => `${API}/documents/clients/download/${fileId}?inline=1`,
  deleteClientDoc: (fileId) => axios.delete(`${API}/documents/clients/${fileId}`),
  // Expediente / Checklist
  getClientChecklist: (clientId) => axios.get(`${API}/documents/clients/checklist`, { params: { client_id: clientId } }),
  getClientsChecklistSummary: () => axios.get(`${API}/documents/clients/checklist-summary`),
  checklistSummaryExcelUrl: () => `${API}/documents/clients/checklist-summary/excel`,
  toggleValidateClientDoc: (fileId, validated) => axios.patch(`${API}/documents/clients/${fileId}/validate`, validated !== undefined ? { validated } : {}),
};

// Dashboard API
export const dashboardApi = {
  getSummary: () => axios.get(`${API}/dashboard/summary`),
  getKPIs: () => axios.get(`${API}/dashboard/kpis`),
  getAlerts: () => axios.get(`${API}/dashboard/alerts`),
  getMonthlyFlow: () => axios.get(`${API}/dashboard/monthly-flow`),
};

export default {
  clients: clientsApi,
  vehicles: vehiclesApi,
  contracts: contractsApi,
  payments: paymentsApi,
  creditors: creditorsApi,
  dashboard: dashboardApi,
};
